import { InjectionToken, Type } from '@angular/core';
import { SseEventPayloadMap, SseEventType } from '@api-types/sse/sse.types';
import { NotificationSseHandler } from '@components/notification/handlers/notification-sse.handler';
import { ExportSseHandler } from 'common/export-center/export-sse.handler';
import { ConsensusCycleInitializationSseHandler } from 'consensus/cycles/services/consensus-cycle-initialization-sse.handler';
import { AnalysisExportSseHandler } from 'demand-forecasting/analysis/services/analysis-export-sse.handler';
import { AnalysisProgressSseHandler } from 'demand-forecasting/analysis/services/analysis-progress-sse.handler';
import { ReportingImportSseHandler } from 'demand-forecasting/reporting/services/reporting-import-sse.handler';
import { ConsensusExportSseHandler } from 'demand-forecasting/store/consensus/consensus-export-sse.handler';
import { ConsensusReconciliationSseHandler } from 'demand-forecasting/store/consensus/consensus-reconciliation-sse.handler';
import { ConsensusTemplateSseHandler } from 'demand-forecasting/store/consensus/consensus-template-sse.handler';
import { ForecastProgressSseHandler } from 'demand-forecasting/store/forecast-version/forecast-progress-sse.handler';

export interface SseEventHandler<T extends SseEventType = SseEventType> {
  readonly eventType: T;
  handle(payload: SseEventPayloadMap[T]): void;
}

export const SSE_EVENT_HANDLER = new InjectionToken<SseEventHandler[]>(
  'SSE_EVENT_HANDLER',
);

/**
 * all event types required to have a handler
 * exception: system events like heartbeat
 */
export type HandledSseEventType = Exclude<
  SseEventType,
  typeof SseEventType.Heartbeat
>;

// config type that enforces a handler for every handled event type
export type SseHandlerConfig = {
  [K in HandledSseEventType]: Type<SseEventHandler<K>>;
};

/**
 * central frontend config mapping for sse events and their handlers
 * add your handler here and it will be automatically registered
 */
export const SSE_HANDLER_CONFIG: SseHandlerConfig = {
  [SseEventType.AnalysisProgress]: AnalysisProgressSseHandler,
  [SseEventType.AnalysisExport]: AnalysisExportSseHandler,
  [SseEventType.ConsensusExport]: ConsensusExportSseHandler,
  [SseEventType.ForecastProgress]: ForecastProgressSseHandler,
  [SseEventType.ReportingImport]: ReportingImportSseHandler,
  [SseEventType.Notification]: NotificationSseHandler,
  [SseEventType.ConsensusReconciliation]: ConsensusReconciliationSseHandler,
  [SseEventType.ConsensusCycleInitialization]:
    ConsensusCycleInitializationSseHandler,
  [SseEventType.ConsensusTemplate]: ConsensusTemplateSseHandler,
  [SseEventType.Export]: ExportSseHandler,
};
